import React, { useContext, useMemo, useState } from 'react';
import { Alert, Select, Space, Spin, Tabs } from 'antd';

import ActivityTab from '@totem/components/ticketing/ticketDashboard/tabs/ActivityTab';
import CustomersTab from '@totem/components/ticketing/ticketDashboard/tabs/CustomersTab';
import FindingsTab from '@totem/components/ticketing/ticketDashboard/tabs/FindingsTab';
import HygieneTab from '@totem/components/ticketing/ticketDashboard/tabs/HygieneTab';
import SlaTab from '@totem/components/ticketing/ticketDashboard/tabs/SlaTab';
import SummaryTab from '@totem/components/ticketing/ticketDashboard/tabs/SummaryTab';
import TicketDashboardContext from '@totem/components/ticketing/ticketDashboard/TicketDashboardContext';
import { DashboardTicket } from '@totem/components/ticketing/ticketDashboard/types';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';

const filterOption = (
  input: string,
  option?: { label: string; value: string },
) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

const TicketDashboardLayout = () => {
  const { loading, data } = useContext(TicketDashboardContext);
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([
    'Incident',
    'Service Request',
  ]);
  const [selectedCats, setSelectedCats] = useState<string[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);

  const allTickets: DashboardTicket[] = useMemo(
    () => data?.tickets ?? [],
    [data],
  );

  const orgOptions = useMemo(
    () =>
      Object.values(data?.orgs ?? {})
        .sort((aOrg, bOrg) => aOrg.name.localeCompare(bOrg.name))
        .map((org) => ({
          value: org.id,
          label: `${org.name} (${org.m.open})`,
        })),
    [data?.orgs],
  );

  const typeOptions = useMemo(
    () =>
      [...new Set(allTickets.map((tkt) => tkt.ty).filter(Boolean))]
        .sort()
        .map((typ) => ({ value: typ, label: typ })),
    [allTickets],
  );

  const catOptions = useMemo(
    () =>
      [...new Set(allTickets.map((tkt) => tkt.cat).filter(Boolean))]
        .sort()
        .map((cat) => ({ value: cat, label: cat })),
    [allTickets],
  );

  const assigneeOptions = useMemo(
    () =>
      [...new Set(allTickets.map((tkt) => tkt.an).filter(Boolean))]
        .sort()
        .map((name) => ({ value: name, label: name })),
    [allTickets],
  );

  const filteredTickets: DashboardTicket[] = useMemo(() => {
    let rows = allTickets;
    if (selectedOrgs.length > 0)
      rows = rows.filter((tkt) => selectedOrgs.includes(tkt.o));
    if (selectedTypes.length > 0)
      rows = rows.filter((tkt) => selectedTypes.includes(tkt.ty));
    if (selectedCats.length > 0)
      rows = rows.filter((tkt) => selectedCats.includes(tkt.cat));
    if (selectedAssignees.length > 0)
      rows = rows.filter((tkt) => selectedAssignees.includes(tkt.an));
    return rows;
  }, [
    allTickets,
    selectedOrgs,
    selectedTypes,
    selectedCats,
    selectedAssignees,
  ]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <Alert type="error" message="Failed to load dashboard data." />;
  }

  const tabItems = [
    {
      key: 'summary',
      label: `Summary`,
      children: (
        <SummaryTab
          tickets={filteredTickets}
          summary={data.summary}
          orgs={data.orgs}
          isFiltered={
            selectedOrgs.length > 0 ||
            selectedTypes.length > 0 ||
            selectedCats.length > 0 ||
            selectedAssignees.length > 0
          }
        />
      ),
    },
    {
      key: 'findings',
      label: `Findings (${filteredTickets.length})`,
      children: (
        <FindingsTab
          tickets={filteredTickets}
          orgs={data.orgs}
          buildings={data.buildings}
        />
      ),
    },
    {
      key: 'customers',
      label: `Customers (${Object.keys(data.orgs).length})`,
      children: (
        <CustomersTab
          orgs={data.orgs}
          onOrgSelect={(id) => {
            setSelectedOrgs([id]);
          }}
        />
      ),
    },
    {
      key: 'activity',
      label: 'Activity',
      children: (
        <ActivityTab tickets={filteredTickets} summary={data.summary} />
      ),
    },
    {
      key: 'hygiene',
      label: 'Hygiene',
      children: <HygieneTab tickets={filteredTickets} orgs={data.orgs} />,
    },
    {
      key: 'sla',
      label: 'SLA Deep-Dive',
      children: <SlaTab tickets={filteredTickets} summary={data.summary} />,
    },
  ];

  return (
    <ContentLayout>
      <IBCard
        isCovered
        title={'Open Tickets Dashboard'}
        extra={
          <Space wrap>
            <Select
              mode="multiple"
              style={{ minWidth: 180, maxWidth: 400 }}
              options={orgOptions}
              value={selectedOrgs}
              onChange={setSelectedOrgs}
              placeholder="All Customers"
              showSearch
              filterOption={filterOption}
              maxTagCount="responsive"
            />
            <Select
              mode="multiple"
              style={{ minWidth: 140, maxWidth: 300 }}
              options={typeOptions}
              value={selectedTypes}
              onChange={setSelectedTypes}
              placeholder="All Types"
              showSearch
              filterOption={filterOption}
              maxTagCount="responsive"
            />
            <Select
              mode="multiple"
              style={{ minWidth: 150, maxWidth: 300 }}
              options={catOptions}
              value={selectedCats}
              onChange={setSelectedCats}
              placeholder="All Categories"
              showSearch
              filterOption={filterOption}
              maxTagCount="responsive"
            />
            <Select
              mode="multiple"
              style={{ minWidth: 160, maxWidth: 320 }}
              options={assigneeOptions}
              value={selectedAssignees}
              onChange={setSelectedAssignees}
              placeholder="All Assignees"
              showSearch
              filterOption={filterOption}
              maxTagCount="responsive"
            />
          </Space>
        }
      >
        <Tabs items={tabItems} />
      </IBCard>
    </ContentLayout>
  );
};

export default TicketDashboardLayout;
